import { useTranslations } from 'next-intl';

import { PositionHistory } from '@flipster/apis/v2/history/types';
import { InstrumentApi } from '@flipster/apis/v2/snapshot';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Badge } from '@flipster/react/design-system/badge';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import TrendIcon from '@flipster/react/icon/generated/TrendIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { TableCell, TableRow } from '@flipster/ui/Table';
import ShareIcon from '@flipster/ui/assets/svgs/share.svg';
import { FeePrecision, getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import { DateFormatter } from '@flipster/utils/datetime';
import segment from '@flipster/utils/segment';

import { LeverageBadge, OrderSideBadge } from '../../../misc/badge';
import { SymbolDisplay } from '../../../misc/display';
import { Pnl, getFormattedPnl } from '../../../shared/pnl';
import { PnlShareModal } from '../../../shared/pnl-share-modal';
import { useRawSnapshotSpec } from '../../../shared/snapshot/use-snapshot-spec';
import { actualExecFeeValueStyles, originalFeeValueStyles } from './pnl-history-modal';
import { useFeeCalculations } from './use-fee-calculations';

interface Props {
  item: PositionHistory;
}

const DesktopRow = ({ item }: Props) => {
  const overlay = useOverlay();
  const { instrumentsKeyBySymbol } = useRawSnapshotSpec();

  const symbolInfo = instrumentsKeyBySymbol?.[item.symbol];

  const realizedPnl = item.netRealizedPnl;
  const realizedPnlPct = item.netRealizedPnlPct;

  const { color } = getFormattedPnl(realizedPnl, realizedPnlPct);

  return (
    <TableRow>
      <TableCell>
        <p>
          <Datetime value={item.openTime} format={DateFormatter.DateTimeSec} css={{ textStyle: 'Legacy/Number3' }} /> -
          <br />
          <Datetime value={item.closeTime} format={DateFormatter.DateTimeSec} css={{ textStyle: 'Legacy/Number3' }} />
        </p>
      </TableCell>
      <TableCell>
        <SymbolDisplay
          symbol={item.symbol}
          icon={<SymbolDisplay.Icon size={24} />}
          mainLabel={<SymbolDisplay.Label value='shortName' css={{ textStyle: 'Text/L/Strong' }} />}
        />
      </TableCell>
      <TableCell>
        <OrderSideBadge orderSide={item.side} />
      </TableCell>
      <TableCell>
        <LeverageBadge leverage={item.initLeverage} marginType={item.isCrossMargin ? 'Cross' : 'Isolated'} />
      </TableCell>
      <TableCell align='right'>
        <div className={flex({ gap: 4, direction: 'column', align: 'flex-end' })}>
          <Tooltip.Root
            placement='bottom'
            onOpenChange={(isOpen) => {
              if (isOpen) {
                segment.track('position_history_realized_pnl_click', {
                  position_id: item.positionId,
                  symbol: item.symbol,
                  realized_pnl: Number(realizedPnl),
                  side: item.side,
                  leverage: item.initLeverage,
                });
              }
            }}
          >
            <Tooltip.TextTrigger
              label={
                <Pnl number={realizedPnl} percent={realizedPnlPct} css={{ textStyle: 'Text/M/Strong' }}>
                  <Pnl.Number currency={symbolInfo?.quoteCurrency} /> <Pnl.Percent />
                </Pnl>
              }
              color={color === 'neutral' ? 'neutral-4' : color}
            />
            <Tooltip.Content>
              <PnlTooltip item={item} currency={symbolInfo?.quoteCurrency} />
            </Tooltip.Content>
          </Tooltip.Root>

          {item.boostApplied && (
            <div>
              <Badge leadingIcon={<TrendIcon />} color='promotion'>
                <FormattedMessage id='Autogen_common.r251126.s_Boost' />
              </Badge>
            </div>
          )}
        </div>

        <ShareIcon
          className={css({ color: 'content.default.level-3', cursor: 'pointer' })}
          onClick={() => {
            overlay.open(({ isOpen, onClose, unmount }) => (
              <PnlShareModal
                isOpen={isOpen}
                onClose={onClose}
                position={{
                  type: 'perpetual',
                  status: 'closed',
                  symbol: item.symbol,
                  side: item.side,
                  entryPrice: item.avgOpenPrice,
                  evaluationPrice: item.avgClosePrice,
                  pnl: realizedPnl,
                  pnlPercentage: realizedPnlPct,
                  initLeverage: item.initLeverage,
                }}
                onExit={unmount}
              />
            ));
          }}
        />
      </TableCell>
      <TableCell align='right'>
        <Formatted.Number value={item.qty} css={{ textStyle: 'Text/M/Monospace/Regular' }} />
      </TableCell>
      <TableCell align='right'>
        <Formatted.Number
          value={item.avgOpenPrice}
          precision={symbolInfo?.pricePrecision}
          toFixed
          suffix={symbolInfo?.quoteCurrency}
          css={{ textStyle: 'Text/M/Monospace/Regular' }}
        />
      </TableCell>
      <TableCell align='right'>
        <Formatted.Number
          value={item.avgClosePrice}
          precision={symbolInfo?.pricePrecision}
          toFixed
          suffix={symbolInfo?.quoteCurrency}
          css={{ textStyle: 'Text/M/Monospace/Regular' }}
        />
      </TableCell>
    </TableRow>
  );
};

export default DesktopRow;

interface PnlTooltipProps {
  item: PositionHistory;
  currency?: InstrumentApi['quoteCurrency'];
}

const PnlTooltip = ({ item, currency = 'USDT' }: PnlTooltipProps) => {
  const t = useTranslations();
  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(currency);

  const {
    feeCalculations,
    hasTradingFeeBonusRedemption,
    tradingFeeBonusRedemptionText,
    hasFundingFeeBonusRedemption,
    fundingFeeBonusRedemptionText,
  } = useFeeCalculations(item);

  return (
    <div className={stack({ gap: 6, minW: 217 })}>
      <div className={css({ textAlign: 'left', textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
        <FormattedMessage id='Autogen_common.r251126.s_Realized_PnL' />
      </div>
      <dl
        className={stack({
          gap: 4,
          '& > div': { display: 'flex', justifyContent: 'space-between', gap: 16 },
          '& dt': {
            flex: 1,
            textStyle: 'Text/S/regular',
            color: 'content.default.level-2',
            textAlign: 'left',
          },
          '& dd': {
            flex: 1,
            textStyle: 'Text/S/regular',
            textAlign: 'right',
          },
        })}
      >
        <div>
          <dt>{t('Common.r251107.Trade_pnl')}</dt>
          <dd>
            <Formatted.Number
              value={item.grossRealizedPnl}
              precision={quoteCurrencyPrecision}
              suffix={currency}
              toFixed
              withColor
              withPlus
            />
          </dd>
        </div>
        <div>
          <dt>{t('Common.r251107.Funding_fee')}</dt>
          <dd>
            {hasFundingFeeBonusRedemption && (
              <div className={hstack({ gap: 4, justify: 'flex-end' })}>
                <Formatted.Number
                  value={feeCalculations.originalFundingFee}
                  precision={FeePrecision}
                  suffix={currency}
                  zeroString='0.00'
                  toFixed
                  css={originalFeeValueStyles}
                />
                <ArrowRightIcon size={14} color={token('colors.content.default.level-2')} />
              </div>
            )}
            <Formatted.Number
              value={feeCalculations.actualFundingFee}
              precision={FeePrecision}
              suffix={currency}
              zeroString='0.00'
              toFixed
              withColor
              withPlus
            />
            {fundingFeeBonusRedemptionText && (
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                {fundingFeeBonusRedemptionText}
              </p>
            )}
          </dd>
        </div>
        <div>
          <dt>{t('Common.r251107.Trading_fee')}</dt>
          <dd>
            {hasTradingFeeBonusRedemption && (
              <div className={hstack({ gap: 4, justify: 'flex-end' })}>
                <Formatted.Number
                  value={feeCalculations.originalTradingFee}
                  precision={FeePrecision}
                  suffix={currency}
                  zeroString='0.00'
                  toFixed
                  css={originalFeeValueStyles}
                />
                <ArrowRightIcon size={14} color={token('colors.content.default.level-2')} />
              </div>
            )}
            <Formatted.Number
              value={feeCalculations.actualTradingFee}
              precision={FeePrecision}
              suffix={currency}
              zeroString='0.00'
              toFixed
              withColor
              withPlus
              css={actualExecFeeValueStyles}
            />
            {tradingFeeBonusRedemptionText && (
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                {tradingFeeBonusRedemptionText}
              </p>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
};
